<template>
  <mm-modal
    class="apply-appointment-modal"
    :title="$t('appointment.applyTitle')"
    width="480px"
    :esc-closable="false"
    :mask-closable="false"
    :visible.sync="localVisible"
    :overflow-auto="true"
  >
    <div class="top-info">
      {{ $t("appointment.applyTip", { time: formatTime }) }}
    </div>
    <mm-form
      ref="applyForm"
      :rules="rules"
      :data="applyParams"
      layout="vertical"
    >
      <mm-form-field
        :label="$t('appointment.company')"
        class="company-field"
      >
        {{ company }}
      </mm-form-field>
      <mm-form-field
        :label="$t('appointment.contact')"
        field="contacts"
        name="contacts"
        required
        :message="$t('appointment.contactPlaceholder')"
      >
        <mm-input v-model="applyParams.contacts" />
      </mm-form-field>
      <mm-form-field
        class="mobile-field"
        :label="$t('appointment.mobile')"
        field="mobile"
        name="mobile"
        required
        :message="$t('appointment.mobilePlaceholder')"
      >
        <mm-input v-model="applyParams.mobile">
          <mm-select
            slot="before"
            v-model="applyParams.country_code"
            :options="countryCodeOption"
            placeholder="区号"
            class="country-code-selector"
            :keymap="{
              value: 'code',
            }"
            show-arrow
            filterable
          >
            <span
              slot="option"
              slot-scope="{ code, name, eng_name }"
              :title="`+${code} (${eng_name}/${name})`"
            >
              {{ `+${code}` }}
            </span>
          </mm-select>
        </mm-input>
      </mm-form-field>
      <mm-form-field
        class="vertify-code-field"
        :label="$t('appointment.verifyCode')"
        field="code"
        required
        :message="$t('appointment.mobilePlaceholder')"
      >
        <div class="mobile-vertify-code">
          <mm-input
            v-model="applyParams.code"
            class="code-input"
            @input="handleInputCode"
          />
          <mm-button
            type="primary"
            class="get-code-btn"
            :disabled="showSmsCountDown"
            @click="sendCode"
          >
            {{ showSmsCountDown ? `(${smsCountDown})s` : $t('appointment.sendVerifyCode') }}
          </mm-button>
        </div>
      </mm-form-field>
      <mm-form-field
        :label="$t('appointment.wechat')"
        field="wechat"
        required
      >
        <div>
          <div>
            <mm-radio
              v-model="wechatRadio"
              name="wechat-radio"
              :value="radioType.PHONE"
            >
              {{ $t('appointment.wechatOptions') }}
            </mm-radio>
          </div>
          <div class="wechat-radio-input">
            <mm-radio
              v-model="wechatRadio"
              name="wechat-radio"
              :value="radioType.CONFIG"
            >
              <mm-input
                class="wechat-input"
                v-model="applyParams.wechat"
              />
            </mm-radio>
          </div>
        </div>
      </mm-form-field>
      <mm-form-field
        :label="$t('appointment.dingding')"
        field="dingding"
        required
      >
        <div>
          <div>
            <mm-radio
              v-model="dingdingRadio"
              name="dingding-radio"
              :value="radioType.PHONE"
            >
              {{ $t('appointment.dingdingOptions') }}
            </mm-radio>
          </div>
          <div class="wechat-radio-input">
            <mm-radio
              v-model="dingdingRadio"
              name="dingding-radio"
              :value="radioType.CONFIG"
            >
              <mm-input
                class="wechat-input"
                v-model="applyParams.dingding"
              />
            </mm-radio>
          </div>
        </div>
      </mm-form-field>
    </mm-form>
    <div slot="footer">
      <mm-button @click="handleCancel">
        {{ $t("appointment.cancel") }}
      </mm-button>
      <mm-button
        type="primary"
        :disabled="submitDisabled"
        @click="handleConfirm"
      >
        {{ $t("appointment.sure") }}
      </mm-button>
    </div>
  </mm-modal>
</template>
<script>
import moment from "moment";
import AppointmentApi from "@api/appointment";
import COUNTRY_CODE from "@utils/countryCode";

import i18n from "@i18n";

const RADIO_TYPE = {
  PHONE: "phone",
  CONFIG: "config",
};

export default {
  name: "ApplyAppointmentModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    info: {
      type: Object,
      default: () => ({}),
    },
    company: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      localVisible: this.visible,
      applyParams: {
        appointment_id: this.info.appointment_id,
        mobile: "",
        contacts: "",
        country_code: "86",
        code: "",
        wechat: "",
        dingding: "",
      },
      submitDisabled: true,
      code: "",
      timer: null,
      smsCountDown: 60,
      showSmsCountDown: false,
      radioType: RADIO_TYPE,
      wechatRadio: RADIO_TYPE.PHONE, // 微信号填写的单选框
      dingdingRadio: RADIO_TYPE.PHONE, // 钉钉号填写的单选框
      rules: {
        mobile: [
          {
            required: true,
            triggers: ["blur"],
            validator: (rule, value, callback) => {
              if (!value) {
                callback(i18n.t("appointment.mobilePlaceholder"));
              } else if (!/^\d{3,20}$/.test(value)) {
                callback(i18n.t("appointment.mobileValidate"));
              }
              callback();
            },
          },
        ],
        contacts: [
          {
            required: true,
            triggers: ["blur"],
            message: i18n.t("appointment.contactPlaceholder"),
          },
        ],
      },
    };
  },
  computed: {
    formatTime() {
      const { date, implementation_time, implementation_time_type } = this.info;
      return `${moment(new Date(date)).format(
        "LL"
      )} ${implementation_time_type}${implementation_time}`;
    },
    countryCodeOption() {
      const arr = COUNTRY_CODE.map((item) => {
        const { code, name, eng_name } = item;
        return {
          label: "+" + code,
          code,
          name,
          eng_name,
        };
      });
      return arr.filter(
        (item, index) =>
          arr.findIndex((codeItem) => codeItem.code === item.code) === index
      );
    },
  },
  watch: {
    visible(oldVal, newVal) {
      this.localVisible = newVal;
    },
  },
  methods: {
    handleCancel() {
      this.localVisible = false;
    },
    handleConfirm() {
      this.$refs.applyForm.validate((err) => {
        if (!err) {
          let applyParams = this.applyParams,
            wechat = "",
            dingding = "";
          if (this.wechatRadio === RADIO_TYPE.CONFIG && applyParams.wechat === '') {
            this.$message.warning(
              i18n.t("appointment.wechatRules")
            );
            return;
          }

          if (this.dingdingRadio === RADIO_TYPE.CONFIG && applyParams.dingding === '') {
            this.$message.warning(
              i18n.t("appointment.dingdingRules")
            );
            return;
          }

          if (this.wechatRadio === RADIO_TYPE.PHONE) {
            wechat = applyParams.mobile;
          } else {
            wechat = applyParams.wechat;
          }
          if (this.dingdingRadio === RADIO_TYPE.PHONE) {
            dingding = applyParams.mobile;
          } else {
            dingding = applyParams.dingding;
          }
          AppointmentApi.applyAppointment({
            ...this.applyParams,
            wechat,
            dingding,
          }).then(() => {
            this.localVisible = false;
            this.$router.push({
              name: "AppointCancel",
            });
          });
        }
      });
    },
    sendCode() {
      if (this.showSmsCountDown) {
        return;
      }
      // 发送验证码倒计时
      const runSmsCountDown = () => {
        this.showSmsCountDown = true;
        let defaultTime = 60;
        let timer = "";
        const timeOut = () => {
          this.smsCountDown = defaultTime;
          timer = setTimeout(() => {
            defaultTime--;
            if (defaultTime <= 0 || !this.showSmsCountDown) {
              clearTimeout(timer);
              this.showSmsCountDown = false;
              this.submitDisabled = true;
              return;
            }
            timeOut();
          }, 1000);
        };
        timeOut();
      };
      this.$refs.applyForm
        .validate({
          names: ["mobile"],
        })
        .then(() => {
          runSmsCountDown();
          AppointmentApi.fetchAppointmentSmsVertifyCode({
            mobile: this.applyParams.mobile,
            countryCode: this.applyParams.country_code,
          }).catch(() => {
            this.showSmsCountDown = false;
          });
        });
    },
    handleInputCode(value) {
      this.submitDisabled = !value || !this.showSmsCountDown;
    },
  },
};
</script>

<style lang="scss">
.apply-appointment-modal {
  .top-info {
    color: #2a2a2a;
  }
  .info-form {
    margin-top: 30px;
  }
  .company-field {
    margin-top: 20px;
    .mm-form-field-control {
      line-height: 1;
    }
  }
  .country-code-selector {
    width: 100px;
  }
  .mobile-field {
    .mm-form-field-explain {
      padding-left: 100px;
    }
  }
  .mobile-vertify-code {
    display: flex;
    justify-content: space-between;
    .code-input {
      width: 70%;
    }
    .get-code-btn {
      width: 118px;
      height: 34px;
    }
  }
  .wechat-radio-input .mm-radio-faux {
    display: flex;
    align-items: center;
    .wechat-input {
      width: 312px;
    }
  }
}
</style>
